/**
 * SUP-ADMIN-UI-01 — TanStack Query hooks for the bug-report triage surface.
 *
 * Wire (support/v1@1.0.0):
 *   GET   /api/support/v1/bug-reports        → listBugReports (filters + cursor)
 *   GET   /api/support/v1/bug-reports/{id}   → getBugReport (full detail + read SAS)
 *   PATCH /api/support/v1/bug-reports/{id}   → patchBugReport (status|assignee|note)
 *
 * These are **platform-admin, cross-tenant** reads/patch — so, unlike the
 * tenant-scoped tenancy hooks, they are NOT `enabled`-gated on tenant
 * readiness and `supportFetch` sends NO `X-Tenant-ID` header.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supportFetch } from "./supportFetch.js";
import { supportKeys } from "./keys.js";
import type {
  BugReport,
  BugReportList,
  BugReportListFilters,
  BugReportPatch,
} from "./types.js";

/** Serialise the filter set into a `support/v1` list query string. */
function buildListQuery(filters: BugReportListFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.tenantId) params.set("tenantId", filters.tenantId);
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.severity) params.set("severity", filters.severity);
  if (filters.createdFrom) params.set("createdFrom", filters.createdFrom);
  if (filters.createdTo) params.set("createdTo", filters.createdTo);
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.limit != null) params.set("limit", String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** GET /bug-reports — one filtered + paged page of triage rows. */
export function useBugReportsQuery(filters: BugReportListFilters) {
  return useQuery<BugReportList>({
    queryKey: supportKeys.list(filters),
    queryFn: () =>
      supportFetch<BugReportList>(`/bug-reports${buildListQuery(filters)}`),
    staleTime: 15_000,
    retry: false,
  });
}

/** GET /bug-reports/{id} — full detail (diagnostics + attachments + read SAS). */
export function useBugReportQuery(id: string | null) {
  return useQuery<BugReport>({
    queryKey: supportKeys.detail(id ?? "none"),
    queryFn: () => {
      if (id == null) throw new Error("bug report id is required");
      return supportFetch<BugReport>(`/bug-reports/${encodeURIComponent(id)}`);
    },
    enabled: id != null,
    staleTime: 0,
    retry: false,
  });
}

/**
 * PATCH /bug-reports/{id} — set status / assignee / resolution note. Returns
 * the updated full report. On success we seed the detail cache with the fresh
 * report and invalidate the list so the row reflects the change on refresh.
 */
export function usePatchBugReportMutation(id: string) {
  const qc = useQueryClient();
  return useMutation<BugReport, Error, BugReportPatch>({
    mutationFn: (patch: BugReportPatch) =>
      supportFetch<BugReport>(`/bug-reports/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: patch,
      }),
    onSuccess: (updated) => {
      qc.setQueryData(supportKeys.detail(id), updated);
      void qc.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
}
